import React from 'react';
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "../../utils/cn";
import Icon from '../AppIcon';

const buttonVariants = cva(
    "inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-semibold ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-95",
    {
        variants: {
            variant: {
                default: "bg-primary text-primary-foreground hover:bg-primary/90 koho-shadow hover:koho-shadow-lg",
                destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-lg hover:shadow-xl",
                outline: "border-2 border-primary/20 bg-card hover:bg-primary/5 hover:border-primary/40 text-primary koho-shadow",
                secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-lg hover:shadow-xl",
                ghost: "hover:bg-primary/10 hover:text-primary text-muted-foreground",
                link: "text-primary underline-offset-4 hover:underline font-medium",
                success: "bg-success text-success-foreground hover:bg-success/90 shadow-lg hover:shadow-xl",
                warning: "bg-warning text-warning-foreground hover:bg-warning/90 shadow-lg hover:shadow-xl",
                danger: "bg-error text-error-foreground hover:bg-error/90 shadow-lg hover:shadow-xl",
            },
            size: {
                default: "h-11 px-6 py-2.5",
                sm: "h-9 rounded-lg px-4 text-sm",
                lg: "h-12 rounded-xl px-8 text-base",
                icon: "h-11 w-11",
                xs: "h-8 rounded-lg px-3 text-xs",
                xl: "h-14 rounded-xl px-12 text-lg",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

const Button = React.forwardRef(({
    className,
    variant,
    size,
    asChild = false,
    children,
    loading = false,
    iconName = null,
    iconPosition = 'left',
    iconSize = null,
    fullWidth = false,
    disabled = false,
    ...props
}, ref) => {
    const Comp = asChild ? Slot : "button";

    // Enhanced icon size mapping based on button size
    const iconSizeMap = {
        xs: 12,
        sm: 14,
        default: 16,
        lg: 18,
        xl: 20,
        icon: 16,
    };

    const calculatedIconSize = iconSize || iconSizeMap?.[size] || 16;

    // Enhanced loading spinner with Koho styling
    const LoadingSpinner = () => (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
    );

    const renderIcon = () => {
        if (!iconName) return null;
        try {
            return (
                <Icon
                    name={iconName}
                    size={calculatedIconSize}
                    className={cn(
                        "transition-transform duration-300",
                        children && iconPosition === 'left' && "mr-2",
                        children && iconPosition === 'right' && "ml-2",
                        "group-hover:scale-110"
                    )}
                />
            );
        } catch {
            return null;
        }
    };

    const renderFallbackButton = () => (
        <button
            className={cn(
                buttonVariants({ variant, size, className }),
                fullWidth && "w-full",
                "group relative overflow-hidden"
            )}
            ref={ref}
            disabled={disabled || loading}
            {...props}
        >
            {/* Background animation effect */}
            {variant === 'default' && (
                <div className="absolute inset-0 bg-gradient-to-r from-primary to-accent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />
            )}
            
            {/* Content */}
            <div className="relative z-10 flex items-center justify-center">
                {loading && <LoadingSpinner />}
                {iconName && iconPosition === 'left' && renderIcon()}
                {children}
                {iconName && iconPosition === 'right' && renderIcon()}
            </div>
        </button>
    );

    // When asChild is true, merge icons into the child element
    if (asChild) {
        try {
            if (!children || React.Children?.count(children) !== 1) {
                return renderFallbackButton();
            }

            const child = React.Children?.only(children);

            if (!React.isValidElement(child)) {
                return renderFallbackButton();
            }
            const content = (
                <>
                    {loading && <LoadingSpinner />}
                    {iconName && iconPosition === 'left' && renderIcon()}
                    {child?.props?.children}
                    {iconName && iconPosition === 'right' && renderIcon()}
                </>
            );

            const clonedChild = React.cloneElement(child, {
                className: cn(
                    buttonVariants({ variant, size, className }),
                    fullWidth && "w-full",
                    "group relative overflow-hidden",
                    child?.props?.className
                ),
                disabled: disabled || loading || child?.props?.disabled,
                children: (
                    <>
                        {/* Background animation effect */}
                        {variant === 'default' && (
                            <div className="absolute inset-0 bg-gradient-to-r from-primary to-accent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />
                        )}
                        <div className="relative z-10 flex items-center justify-center">
                            {content}
                        </div>
                    </>
                )
            });

            return <Comp ref={ref} {...props}>{clonedChild}</Comp>;
        } catch {
            return renderFallbackButton();
        }
    }

    return (
        <Comp
            className={cn(
                buttonVariants({ variant, size, className }),
                fullWidth && "w-full",
                "group relative overflow-hidden"
            )}
            ref={ref}
            disabled={disabled || loading}
            {...props}
        >
            {/* Background animation effect */}
            {variant === 'default' && (
                <div className="absolute inset-0 bg-gradient-to-r from-primary to-accent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />
            )}
            
            {/* Content */}
            <div className="relative z-10 flex items-center justify-center">
                {loading && <LoadingSpinner />}
                {iconName && iconPosition === 'left' && renderIcon()}
                {children}
                {iconName && iconPosition === 'right' && renderIcon()}
            </div>
        </Comp>
    );
});

Button.displayName = "Button";
export default Button;